/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getArrayDecoder,
  getArrayEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type Account,
  type Address,
  type EncodedAccount,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type ReadonlyUint8Array,
} from '@solana/kit';

export const COSIGNER_CONFIG_DISCRIMINATOR = new Uint8Array([
  133, 227, 177, 74, 145, 74, 247, 183,
]);

export function getCosignerConfigDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    COSIGNER_CONFIG_DISCRIMINATOR,
  );
}

export type CosignerConfig = {
  discriminator: ReadonlyUint8Array;
  adminAuthority: Address;
  cosignerCount: number;
  bump: number;
  version: number;
  reserved: ReadonlyUint8Array;
  cosigners: Array<Address>;
};

export type CosignerConfigArgs = {
  adminAuthority: Address;
  cosignerCount: number;
  bump: number;
  version: number;
  reserved: ReadonlyUint8Array;
  cosigners: Array<Address>;
};

/** Gets the encoder for {@link CosignerConfigArgs} account data. */
export function getCosignerConfigEncoder(): FixedSizeEncoder<CosignerConfigArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['adminAuthority', getAddressEncoder()],
      ['cosignerCount', getU8Encoder()],
      ['bump', getU8Encoder()],
      ['version', getU8Encoder()],
      ['reserved', fixEncoderSize(getBytesEncoder(), 37)],
      ['cosigners', getArrayEncoder(getAddressEncoder(), { size: 32 })],
    ]),
    (value) => ({ ...value, discriminator: COSIGNER_CONFIG_DISCRIMINATOR }),
  );
}

/** Gets the decoder for {@link CosignerConfig} account data. */
export function getCosignerConfigDecoder(): FixedSizeDecoder<CosignerConfig> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['adminAuthority', getAddressDecoder()],
    ['cosignerCount', getU8Decoder()],
    ['bump', getU8Decoder()],
    ['version', getU8Decoder()],
    ['reserved', fixDecoderSize(getBytesDecoder(), 37)],
    ['cosigners', getArrayDecoder(getAddressDecoder(), { size: 32 })],
  ]);
}

/** Gets the codec for {@link CosignerConfig} account data. */
export function getCosignerConfigCodec(): FixedSizeCodec<
  CosignerConfigArgs,
  CosignerConfig
> {
  return combineCodec(getCosignerConfigEncoder(), getCosignerConfigDecoder());
}

export function decodeCosignerConfig<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>,
): Account<CosignerConfig, TAddress>;
export function decodeCosignerConfig<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>,
): MaybeAccount<CosignerConfig, TAddress>;
export function decodeCosignerConfig<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>,
): Account<CosignerConfig, TAddress> | MaybeAccount<CosignerConfig, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getCosignerConfigDecoder(),
  );
}

export async function fetchCosignerConfig<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<Account<CosignerConfig, TAddress>> {
  const maybeAccount = await fetchMaybeCosignerConfig(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeCosignerConfig<
  TAddress extends string = string,
>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<MaybeAccount<CosignerConfig, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeCosignerConfig(maybeAccount);
}

export async function fetchAllCosignerConfig(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<Account<CosignerConfig>[]> {
  const maybeAccounts = await fetchAllMaybeCosignerConfig(
    rpc,
    addresses,
    config,
  );
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeCosignerConfig(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<MaybeAccount<CosignerConfig>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) =>
    decodeCosignerConfig(maybeAccount),
  );
}

export function getCosignerConfigSize(): number {
  return 1104;
}
